import formatCustomDateTime from 'intl-dateformat';

import { config } from '../constants';
import { Language, toBcp47 } from '../i18n';
import { isValidDate, isValidNumber } from 'common/utils'; // Exception: we should not import common from core
import {
  NumerFormatterConfig,
  NumberFormatOptions,
  NumberFormatFunction,
  DateTimeFormatterConfig,
  DateTimeFormatOptions,
  DateTimeFormatFunction,
  DateTimeCustomFormatFunction,
} from './localization.model';
import {
  isIntlAPISupported,
  getNumberFormatterFallback,
  getDateTimeFormatterFallback,
} from './localization.fallbacks';

/**
 * Number formatting business logic
 */
export const createNumberFormatter = (
  language?: Language | string,
  configuration?: NumerFormatterConfig
): NumberFormatFunction => {
  const locale = language ? toBcp47(language) : language;

  // Sanity check, Intl browser support, if not, default fallback.
  const formatFunction = isIntlAPISupported('NumberFormat')
    ? Intl.NumberFormat(locale, {
        // Default configuration
        maximumFractionDigits: config.FORMAT_NUMBER_REGULAR_FRACTION_DIGITS,
        useGrouping: config.FORMAT_NUMBER_USE_GROUPING,
        // Input configuration
        ...configuration,
      }).format
    : getNumberFormatterFallback(locale);

  return (n: number, options?: NumberFormatOptions) => {
    if (isValidNumber(n)) {
      return formatFunction(n);
    } else {
      return options?.nullable ? config.FORMAT_NULLABLE_CHARACTER : '';
    }
  };
};

/**
 * Date&Time formatting business logic
 */
export const createDateTimeNativeFormatter = (
  language?: Language | string,
  configuration?: DateTimeFormatterConfig
): DateTimeFormatFunction => {
  const locale = language ? toBcp47(language) : language;

  // Sanity check, Intl browser support, if not, default fallback.
  const formatFunction = isIntlAPISupported('DateTimeFormat')
    ? Intl.DateTimeFormat(locale, configuration).format
    : getDateTimeFormatterFallback(locale);

  return (d: Date, options?: DateTimeFormatOptions) => {
    if (isValidDate(d)) {
      return formatFunction(d);
    } else {
      return options?.nullable ? config.FORMAT_NULLABLE_CHARACTER : '';
    }
  };
};

export const createDateTimeCustomFormatter = (
  language?: Language | string
): DateTimeCustomFormatFunction => {
  const locale = language ? toBcp47(language) : language;

  // Sanity check, Intl browser support, if not, default fallback.
  const formatFunction = isIntlAPISupported('DateTimeFormat')
    ? (d: Date, format: string) => formatCustomDateTime(d, format, { locale })
    : getDateTimeFormatterFallback(locale);

  return (d: Date, format: string, options?: DateTimeFormatOptions) => {
    if (isValidDate(d)) {
      return formatFunction(d, format);
    } else {
      return options?.nullable ? config.FORMAT_NULLABLE_CHARACTER : '';
    }
  };
};
